import type { FormikProps } from 'formik'
import { Form, Formik } from 'formik'
import React, { useCallback, useEffect, useRef } from 'react'

import { Dashboard, PageLayout } from '@components/common'
import { SideSummary } from '@components/common/SideSummary'
import {
  BackButton,
  Button,
  ButtonClean,
  Icon,
  InfoTooltip,
  List,
  PriceString,
  Skeleton,
  Subsection,
  useModalContext,
} from '@fourthwall/components'
import { PlusIcon } from '@fourthwall/icons'
import type { OfferApi } from '@fourthwall/services/dist/OffersApiClient'
import type { Money } from '@fourthwall/services/dist/models/common'
import { routing } from '@utils/routing'

import * as S from './SamplesOrder.styled'
import type { FormValues, OrderItem, Summary } from './SamplesOrders.types'
import { SamplesOrdersListItem } from './components/SamplesOrdersListItem/SamplesOrdersListItem'
import { validationSchema } from './consts'
import { SelectItemsModalContainer } from './modules/SelectItemsModal'
import { createListData } from './utils/createListData'
import { getCounterText } from './utils/getCounterText'
import { mapValuesToSummary } from './utils/mapValuesToSummary'
import { removeItemFromValues } from './utils/removeItemFromValues'

type SamplesOrderViewProps = {
  credit: Money | undefined
  offers: OfferApi.GetOffersResponse['offers'] | undefined
  initialValues?: FormValues
  button: { loading: boolean }
  showModal?: boolean
  onSubmit: (values: FormValues) => void
}

export const SamplesOrderView = ({
  credit,
  offers,
  button,
  initialValues = { items: [] },
  showModal = false,
  onSubmit,
}: SamplesOrderViewProps) => {
  const { open, close } = useModalContext()
  const formRef = useRef<FormikProps<FormValues>>(null)

  const createOpenModal = useCallback(
    (values: FormValues, setValues: (values: FormValues) => void) => () => {
      open(SelectItemsModalContainer, {
        initialValues: values,
        onSubmit: (newValues) => {
          setValues(newValues)
          close()
        },
      })
    },
    [open, close],
  )

  useEffect(() => {
    if (!showModal) return

    if (!formRef.current) return

    createOpenModal(formRef.current.values, formRef.current.setValues)()
  }, [showModal, createOpenModal])

  const handleRemoveItemButtonClick =
    (values: FormValues, setValues: (values: FormValues) => void) => (item: any) => {
      const newValues = removeItemFromValues(values, item.id)

      setValues(newValues)
    }

  const renderSummaryContent = (summary: Summary) => {
    if (summary.items <= 0) {
      return (
        <S.SummaryItem>
          <div>Add items to the cart</div>
        </S.SummaryItem>
      )
    }

    return (
      <>
        <S.SummaryItem>
          <div>
            Subtotal ({summary.items} {summary.items === 1 ? 'item' : 'items'})
          </div>
          <div>
            <PriceString value={summary.subtotal} />
          </div>
        </S.SummaryItem>
        <S.SummaryItem>
          <div>Shipping</div>
          <div>-</div>
        </S.SummaryItem>
        {!!summary.samplesCredit && (
          <S.SummaryItem>
            <div>Samples credit</div>
            <div>
              <PriceString value={summary.samplesCredit} />
            </div>
          </S.SummaryItem>
        )}
        {!!summary.profitsBalance && (
          <S.SummaryItem>
            <S.SummaryItemTooltipLabelContainer>
              <div>Profits balance</div>
              <S.SummaryItemTooltipLabelTooltipContainer>
                <InfoTooltip content="When samples credit do not cover the order, you pay for samples out of your profits" />
              </S.SummaryItemTooltipLabelTooltipContainer>
            </S.SummaryItemTooltipLabelContainer>
            <div>
              <PriceString value={summary.profitsBalance} />
            </div>
          </S.SummaryItem>
        )}

        <S.SummaryDivider />
        <S.SummaryItem>
          <div>Total to pay</div>
          <div>
            <PriceString value={summary.total} />
          </div>
        </S.SummaryItem>

        <S.SummaryFooter>+ Shipping and taxes calculated at checkout</S.SummaryFooter>
      </>
    )
  }

  const renderSummary = (values: FormValues) => {
    if (!credit || !offers) return null

    const summary = mapValuesToSummary(values, credit, offers)

    if (!summary) return null

    return (
      <SideSummary>
        <div style={{ marginBottom: 8 }}>
          <p style={{ fontWeight: 600, fontSize: 16, lineHeight: 1.5 }}>Order summary</p>
        </div>
        <div>{renderSummaryContent(summary)}</div>
        <div style={{ marginTop: 16 }}>
          <Button
            label="Order now"
            type="submit"
            appearance="primary"
            fullWidth
            disabled={!(summary.items > 0) || !formRef?.current?.isValid}
            loading={button.loading}
            analytics="samplesOrderNow"
          />
        </div>
      </SideSummary>
    )
  }

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      innerRef={formRef}
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={onSubmit}
    >
      {({ values, setValues }) => (
        <Form noValidate>
          <PageLayout
            leftContent={
              <Subsection title="Your cart">
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <p style={{ lineHeight: 1.5 }}>{getCounterText(values)}</p>
                  </div>
                  <div>
                    <Button
                      label="Add items"
                      size="small"
                      leftIcon={<Icon component={PlusIcon} height={12} />}
                      onClick={createOpenModal(values, setValues)}
                    />
                  </div>
                </div>
                {!!values.items.length && (
                  <div>
                    <List
                      data={createListData(values, offers)}
                      topBorder={false}
                      renderItem={(orderItem: OrderItem, index: number) => (
                        <SamplesOrdersListItem
                          orderItem={orderItem}
                          index={index}
                          quantity={values.items[index].quantity.toString()}
                          onRemove={() => handleRemoveItemButtonClick(values, setValues)(orderItem)}
                          onSetQuantity={(quantity) => {
                            values.items[index].quantity = Number(quantity)
                            setValues({ ...values })
                          }}
                        />
                      )}
                    />
                  </div>
                )}
              </Subsection>
            }
            rightContent={renderSummary(values)}
          >
            <Dashboard.Section
              name="Order samples"
              topContent={<BackButton label="Back" to={routing.settings.samples.self} />}
              title={
                <>
                  Order pre-production samples of your products for quality checks and promotion.
                  You can use sample credit (if available) or a credit card to pay for your samples.{' '}
                  <ButtonClean
                    label="Learn more"
                    href={routing.external.helpCenter.orderingSamples}
                    target="_blank"
                  />
                </>
              }
            >
              <S.SectionContentContainer>
                {credit ? (
                  <p style={{ marginTop: 8 - 24, fontWeight: 600 }}>
                    Your current samples credit: <PriceString value={credit.value} />
                  </p>
                ) : (
                  <Skeleton>
                    <Skeleton.Shape width={200} />
                  </Skeleton>
                )}
              </S.SectionContentContainer>
            </Dashboard.Section>
          </PageLayout>
        </Form>
      )}
    </Formik>
  )
}
